import React, { useState, useEffect, useCallback } from 'react';
import { History, BadgeCheck, AlertTriangle, Clock, HeartHandshake, Gift, Ban, RefreshCw } from 'lucide-react';
import { puntovService } from '../../../services/api';
import Modal from './Modal';
import { fmtUsd, fmtFecha, fmtFechaHora, fmtPeriodo } from './formato';

// Cómo se muestra cada estado del mes. Lo calcula Punto V; aquí solo se pinta.
const ESTADOS = {
  pagado: { etiqueta: 'Pagado', icono: BadgeCheck, clase: 'text-green-700 bg-green-50 border-green-200' },
  en_mora: { etiqueta: 'Vencido', icono: AlertTriangle, clase: 'text-red-700 bg-red-50 border-red-200' },
  pendiente: { etiqueta: 'Pendiente', icono: Clock, clase: 'text-amber-700 bg-amber-50 border-amber-200' },
  exonerado: { etiqueta: 'Exonerado', icono: HeartHandshake, clase: 'text-violet-700 bg-violet-50 border-violet-200' },
  cortesia: { etiqueta: 'Cortesía', icono: Gift, clase: 'text-teal-700 bg-teal-50 border-teal-200' },
  anulado: { etiqueta: 'Anulado', icono: Ban, clase: 'text-gray-600 bg-gray-50 border-gray-200' }
};

const ADEUDADO = ['pendiente', 'en_mora'];

const fmtBs = (v) => `Bs ${Number(v).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Tarjeta = ({ titulo, monto, detalle, clase }) => (
  <div className={`rounded-xl border p-3 ${clase}`}>
    <div className="text-xs font-medium opacity-80">{titulo}</div>
    <div className="text-lg font-bold tabular-nums">{fmtUsd(monto)}</div>
    <div className="text-xs opacity-80">{detalle}</div>
  </div>
);

const meses = (n) => (n === 1 ? '1 mes' : `${n} meses`);

// Trazabilidad del cobro de un cliente: cada mensualidad con su estado, su
// cotización y los pagos que la cubrieron. Los meses adeudados se pueden
// exonerar: Punto V anula el cobro y el ERP rechaza su cotización.
const DialogoHistorialCobros = ({ cliente, puedeExonerar, onClose, onCambio }) => {
  const [historial, setHistorial] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [exonerando, setExonerando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const res = await puntovService.historialCobros(cliente.base_datos);
      setHistorial(res.data);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el historial');
    } finally {
      setCargando(false);
    }
  }, [cliente.base_datos]);

  useEffect(() => { cargar(); }, [cargar]);

  const exonerar = async (e) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const res = await puntovService.otorgarCortesia(cliente.base_datos, exonerando.periodo, `Exoneración: ${motivo.trim()}`);
      const errorCotizacion = res.data?.cotizacion?.error;
      setAviso(`Mensualidad de ${fmtPeriodo(exonerando.periodo)} exonerada (${fmtUsd(exonerando.total_usd)}).`
        + (res.data?.cotizacion?.numero ? ` La cotización ${res.data.cotizacion.numero} quedó rechazada.` : '')
        + (errorCotizacion ? ` Atención: no se pudo rechazar la cotización (${errorCotizacion}); hay que rechazarla a mano.` : ''));
      setExonerando(null);
      setMotivo('');
      await cargar();
      onCambio?.();
    } catch (err) {
      setError(err.message || 'No se pudo exonerar la mensualidad');
    } finally {
      setGuardando(false);
    }
  };

  const r = historial?.resumen;

  return (
    <Modal
      titulo={`Historial de cobros de ${cliente.slug}`}
      subtitulo={`${cliente.duenio.nombre} · ${cliente.base_datos}`}
      icono={History}
      onClose={onClose}
      bloqueado={guardando}
      ancho="xl"
      pie={(
        <>
          <button type="button" onClick={cargar} disabled={cargando || guardando} className="px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-100 flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw size={14} className={cargando ? 'animate-spin' : ''} /> Actualizar
          </button>
          <button type="button" onClick={onClose} disabled={guardando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">Cerrar</button>
        </>
      )}
    >
      {cargando && !historial && (
        <div className="text-center py-10"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
      )}

      {r && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Tarjeta titulo="Pagado" monto={r.pagado_usd} detalle={meses(r.meses_pagados)} clase="border-green-200 bg-green-50 text-green-800" />
            <Tarjeta titulo="Vencido" monto={r.vencido_usd} detalle={meses(r.meses_vencidos)} clase="border-red-200 bg-red-50 text-red-800" />
            <Tarjeta
              titulo="Por vencer"
              monto={(Number(r.adeudado_usd) - Number(r.vencido_usd)).toFixed(2)}
              detalle={meses(r.meses_adeudados - r.meses_vencidos)}
              clase="border-amber-200 bg-amber-50 text-amber-800"
            />
            <Tarjeta titulo="Exonerado" monto={r.exonerado_usd} detalle={`${meses(r.meses_exonerados)}${r.meses_cortesia ? ` · ${r.meses_cortesia} en cortesía` : ''}`} clase="border-violet-200 bg-violet-50 text-violet-800" />
          </div>

          {r.meses_adeudados > 0 && (
            <p className="text-sm text-gray-700">
              Debe <strong>{fmtUsd(r.adeudado_usd)}</strong> en {meses(r.meses_adeudados)}.
              {r.meses_vencidos > 0 && ' Mientras quede un mes vencido tiene las recargas suspendidas.'}
            </p>
          )}

          {aviso && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{aviso}</div>}

          {historial.meses.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">Todavía no tiene mensualidades emitidas.</p>
          ) : (
            <ol className="relative border-l border-gray-200 ml-2 space-y-3">
              {historial.meses.map((m) => {
                const estado = ESTADOS[m.estado] || ESTADOS.anulado;
                const adeudado = ADEUDADO.includes(m.estado);
                return (
                  <li key={m.periodo} className="ml-4">
                    <span className={`absolute -left-[9px] mt-3 w-4 h-4 rounded-full border-2 border-white ${estado.clase.split(' ').find(c => c.startsWith('bg-'))}`} />
                    <div className="rounded-xl border border-gray-200 p-3 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">{fmtPeriodo(m.periodo)}</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${estado.clase}`}>
                            <estado.icono size={12} /> {estado.etiqueta}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold tabular-nums text-gray-800">{fmtUsd(m.total_usd)}</span>
                          {Number(m.recargo_mora_usd) > 0 && <span className="block text-xs text-red-600">incluye {fmtUsd(m.recargo_mora_usd)} de mora</span>}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        {m.cotizacion_numero && <span>Cotización {m.cotizacion_numero}</span>}
                        {m.fecha_emision && <span>Emitida {fmtFecha(m.fecha_emision)}</span>}
                        {m.fecha_limite && <span>Límite {fmtFecha(m.fecha_limite)}</span>}
                        {adeudado && m.dias_restantes !== null && (
                          <span className={m.dias_restantes < 0 ? 'text-red-600' : ''}>
                            {m.dias_restantes < 0 ? `vencida hace ${-m.dias_restantes} día(s)` : `quedan ${m.dias_restantes} día(s)`}
                          </span>
                        )}
                      </div>

                      {m.pagos.map((p, i) => (
                        <div key={i} className="text-xs rounded-lg bg-green-50/60 border border-green-100 px-2.5 py-1.5 text-green-800">
                          Pagado el {fmtFecha(p.fecha_pago)} · Ref. {p.referencia} · {p.moneda === 'VES' ? fmtBs(p.monto) : fmtUsd(p.monto)}
                          {p.tasa_bcv && ` · tasa ${Number(p.tasa_bcv).toLocaleString('es-VE')}`}
                          {p.meses_del_pago?.length > 1 && ` · mismo pago que ${p.meses_del_pago.filter(x => x !== m.periodo).map(fmtPeriodo).join(', ')}`}
                        </div>
                      ))}

                      {m.cortesia && (
                        <div className="text-xs rounded-lg bg-violet-50/60 border border-violet-100 px-2.5 py-1.5 text-violet-800">
                          {m.estado === 'exonerado' ? 'Exonerada' : 'Cortesía'} el {fmtFechaHora(m.cortesia.fecha)}
                          {m.cortesia.otorgada_por && ` por ${m.cortesia.otorgada_por}`} · {m.cortesia.motivo}
                        </div>
                      )}

                      {adeudado && puedeExonerar && exonerando?.periodo !== m.periodo && (
                        <button
                          type="button"
                          onClick={() => { setAviso(''); setMotivo(''); setExonerando(m); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50"
                        >
                          <HeartHandshake size={12} /> Exonerar este mes
                        </button>
                      )}

                      {exonerando?.periodo === m.periodo && (
                        <form onSubmit={exonerar} className="rounded-lg border border-violet-200 bg-violet-50 p-3 space-y-2">
                          <p className="text-xs text-violet-900">
                            Se anula el cobro de {fmtUsd(m.total_usd)}{m.cotizacion_numero ? ` y la cotización ${m.cotizacion_numero} queda rechazada` : ''}.
                            Si era el único mes vencido, se le reactivan las recargas. No se puede deshacer.
                          </p>
                          <textarea
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            rows={2}
                            maxLength={200}
                            required
                            autoFocus
                            placeholder="Motivo de la exoneración (obligatorio)"
                            className="w-full px-3 py-2 border border-violet-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-violet-400 outline-none"
                          />
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setExonerando(null)} disabled={guardando} className="px-3 py-1.5 text-xs text-gray-700 rounded-lg hover:bg-white">Cancelar</button>
                            <button type="submit" disabled={guardando || motivo.trim().length < 5} className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
                              {guardando ? 'Exonerando…' : `Exonerar ${fmtPeriodo(m.periodo)}`}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {error && <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
    </Modal>
  );
};

export default DialogoHistorialCobros;
